/**
 * h:mm / HH:mm math for the flight log drawer.
 *
 * - Clock times ("HH:mm", UTC) are points in the day; differences wrap past
 *   midnight (legs never exceed 24h).
 * - Durations ("h:mm") are unbounded — airframe totals run into the thousands
 *   of hours.
 */

/**
 * Parse an "h:mm" duration (or "HH:mm" clock) into minutes. Lenient: bare
 * digits count as whole hours ("1" → 60, "13" → 780) and single-digit minutes
 * are accepted ("1:3" → 63) — so running totals compute while the user is
 * still typing. Null on bad input.
 */
export function parseHmm(value: string | null | undefined): number | null {
  if (!value) return null
  const match = /^(\d{1,5})(?::(\d{1,2}))?$/.exec(value.trim())
  if (!match) return null
  const minutes = match[2] !== undefined ? parseInt(match[2], 10) : 0
  if (minutes > 59) return null
  return parseInt(match[1], 10) * 60 + minutes
}

/** Format minutes as an "h:mm" duration (e.g. 374 → "6:14"). */
export function formatHmm(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes))
  const h = Math.floor(safe / 60)
  const m = safe % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

/**
 * Stat-card hours: minutes rounded to whole hours with thousands separators
 * (2,580,000 minutes → "43,000"). Pairs with formatWholeNumber for distance.
 */
export function formatWholeHours(minutes: number): string {
  return formatWholeNumber(minutes / 60)
}

/** Whole number with thousands separators (2090393 → "2,090,393"). */
export function formatWholeNumber(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('en-US')
}

/**
 * Minutes from one "HH:mm" clock time to another, wrapping past midnight
 * (23:30 → 00:40 = 70). Null if either side is missing/invalid.
 */
export function diffClock(from: string | null | undefined, to: string | null | undefined): number | null {
  const fromMin = parseHmm(from)
  const toMin = parseHmm(to)
  if (fromMin === null || toMin === null) return null
  return (toMin - fromMin + 1440) % 1440
}

/** Sum two "h:mm" durations. Null if either is missing/invalid. */
export function addHmm(a: string | null | undefined, b: string | null | undefined): string | null {
  const aMin = parseHmm(a)
  const bMin = parseHmm(b)
  if (aMin === null || bMin === null) return null
  return formatHmm(aMin + bMin)
}

/** "HH:mm" (UTC) from a Date, for prefilling clock fields from leg times. */
export function clockFromDate(date: Date | string | null | undefined): string | null {
  if (!date) return null
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return null
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}
